import { SignJWT } from 'jose';
import type { cookies as CookiesType } from 'next/headers';
import type { NextAuthOptions } from 'next-auth';
import GoogleProvider from 'next-auth/providers/google';

import { useSecureCookies } from '@/contexts/environment';

export const authOptions = (cookies: typeof CookiesType): NextAuthOptions => ({
  useSecureCookies,
  providers: [
    ...(process.env.GOOGLE_CLIENT_ID && process.env.GOOGLE_CLIENT_SECRET
      ? [
          GoogleProvider({
            clientId: process.env.GOOGLE_CLIENT_ID,
            clientSecret: process.env.GOOGLE_CLIENT_SECRET,
          }),
        ]
      : []),
  ],
  session: {
    strategy: 'jwt',
    maxAge: 24 * 60 * 60, // ** 1 day
  },
  pages: {
    signIn: '/login',
  },
  callbacks: {
    async signIn(params) {
      if (!params.user.email?.includes('eon.io')) {
        return false;
      }

      if (process.env.IS_DEMO === 'true') {
        await setDocsCookie(cookies);
      }

      return true;
    },
  },
});

export function getParsedJwt(token: string | undefined = '') {
  if (typeof token === 'string') {
    const parts = token.split('.');
    if (parts.length > 1) {
      const payload = parts[1];
      try {
        return JSON.parse(atob(payload));
      } catch {
        return undefined;
      }
    }
  }

  return undefined;
}

export function shouldRefreshToken(authToken: string) {
  const jwt = getParsedJwt(authToken);
  const exp = jwt?.exp;
  if (typeof exp === 'number') {
    const now = Math.floor(Date.now() / 1000);
    // if token expires in 2 minutes, refresh it
    if (exp > now + 120) {
      return false;
    }
  }

  return true;
}

const encoder = new TextEncoder();
const setDocsCookie = async (cookies: typeof CookiesType) => {
  const fern_token = await mintJwtToken();
  const cookieJar = await cookies();
  cookieJar.set('fern_token', fern_token, {
    maxAge: 24 * 60 * 60,
    secure: true,
    sameSite: 'lax',
    domain: 'eon.io',
    path: '/',
    httpOnly: true,
  });
};

async function mintJwtToken() {
  return await new SignJWT({
    fern: {
      roles: ['eon_user', 'employee'],
    },
  })
    .setProtectedHeader({ alg: 'HS256', typ: 'JWT' })
    .setIssuedAt()
    .setExpirationTime('1d')
    .setIssuer('https://eon.io')
    .sign(encoder.encode(process.env.FERN_JWT_SECRET));
}
